import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { Observable } from 'rxjs';
import { filter, withLatestFrom } from 'rxjs/operators';

import {
  HandoverActionTypes,
  HandoverChangeSectionOrdinals,
  HandoverCreateRevision,
  HandoverCreateSection,
  HandoverCreateSectionSuccess,
  HandoverDeleteSection,
  HandoverFileDelete,
  HandoverFileUpload,
  HandoverLoad,
  HandoverLoadRevisions,
  HandoverLoadRevisionsSuccess,
  HandoverLoadSuccess,
  HandoverPatch,
  HandoverSaveSection,
  HandoverSaveSections,
  HandoverSectionTemplatesLoadSuccess,
  HandoverSoftDelete,
  HandoverUpdateFrontPage,
  HandoverUpdateRow,
  HandoverUpdateRowSuccess,
  HandoverUpdateSectionsSuccess,
  HandoverUpdateSignature,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCpId, getCurrentHandoverId, getCurrentProject, getHandoverSectionTemplates } from '@completion/selectors';
import { HandoverService } from '@completion/services';
import { HandoverPart, HandoverSectionTemplate, Project } from '@completion/models';
import { ResourceEffect } from './resource.effect';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class HandoverEffect extends ResourceEffect {
  constructor(
    public actions$: Actions,
    private handoverService: HandoverService,
    public store: Store<State>,
    public toastr: NotificationService,
  ) {
    super(actions$, handoverService, store, toastr);
  }

  loadHandover$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverLoad>(HandoverActionTypes.HandoverLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!currentProject && typeof cpId === 'number' && Number.isFinite(cpId) && cpId > 0),
      trackResource<HandoverPart[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'getHandover',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: (response) => new HandoverLoadSuccess(response),
      }),
    ),
  );

  loadSectionTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverLoad>(HandoverActionTypes.HandoverSectionTemplatesLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      trackResource<HandoverSectionTemplate[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'getSectionTemplates',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: (response) => new HandoverSectionTemplatesLoadSuccess(response),
      }),
    ),
  );

  changeSectionOrdinals$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverChangeSectionOrdinals>(HandoverActionTypes.HandoverChangeSectionOrdinals),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      trackResource<HandoverSectionTemplate[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'updateSectionOrdinals',
        serviceArgs: ([action, currentProject, cpId]) => [
          currentProject.id,
          cpId,
          action.sections.map((section, index) => ({
            id: section.id,
            ordinal: index + action.startIndex + 1,
            title: section.title,
            rows: section.rows,
          })),
        ],
        onSuccess: (response) => new HandoverUpdateSectionsSuccess(response),
      }),
    ),
  );

  uploadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverFileUpload>(HandoverActionTypes.HandoverFileUpload),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentHandoverId) as Observable<number>,
      ),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'fileUpload',
        serviceArgs: ([action, currentProject, cpId, handoverId]) => [currentProject.id, cpId, handoverId, action.rowId, action.file],
        onSuccess: (response) => new HandoverUpdateRowSuccess(response as any),
      }),
    ),
  );

  deleteFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverFileDelete>(HandoverActionTypes.HandoverFileDelete),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentHandoverId) as Observable<number>,
      ),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'fileDelete',
        serviceArgs: ([action, currentProject, cpId, handoverId]) => [currentProject.id, cpId, handoverId, action.rowId, action.file],
        onSuccess: (response) => new HandoverUpdateRowSuccess(response as any),
      }),
    ),
  );

  createSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverCreateSection>(HandoverActionTypes.HandoverCreateSection),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getHandoverSectionTemplates) as Observable<HandoverSectionTemplate[]>,
      ),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'createSection',
        serviceArgs: ([_, currentProject, cpId, sectionTemplates]) => [
          currentProject.id,
          cpId,
          {
            id: null,
            title: 'Other',
            ordinal: (sectionTemplates as HandoverSectionTemplate[]).length + 1,
            rows: null,
          },
        ],
        onSuccess: (response) => new HandoverCreateSectionSuccess(response as any),
      }),
    ),
  );

  saveSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverSaveSection>(HandoverActionTypes.HandoverSaveSection),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'updateSection',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.section],
        onSuccess: (response) => new HandoverUpdateSectionsSuccess(response as any),
      }),
    ),
  );

  saveSections$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverSaveSections>(HandoverActionTypes.HandoverSaveSections),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'updateSections',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.sections],
        onSuccess: (response) => new HandoverUpdateSectionsSuccess(response as any),
      }),
    ),
  );

  updateRow$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverUpdateRow>(HandoverActionTypes.HandoverUpdateRow),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId, getCurrentHandoverId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'updateRow',
        serviceArgs: ([action, currentProject, cpId, handoverId]) => [
          currentProject.id,
          cpId,
          handoverId,
          action.sectionId,
          action.rowId,
          action.rowChanges,
        ],
        onSuccess: (response) => new HandoverUpdateRowSuccess(response as any),
      }),
    ),
  );

  deleteSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverDeleteSection>(HandoverActionTypes.HandoverDeleteSection),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'deleteSection',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.sectionId],
        onSuccess: (response) => new HandoverSectionTemplatesLoadSuccess([response] as any),
      }),
    ),
  );

  softDeleteSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverSoftDelete>(HandoverActionTypes.HandoverSoftDelete),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'softDeleteHandover',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.handoverId],
        onSuccess: (response) => new HandoverLoadSuccess([response] as any),
      }),
    ),
  );

  updateHandoverSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverUpdateSignature>(HandoverActionTypes.HandoverUpdateSignature),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'updateHandoverSignature',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.data.handoverId, action.data.signing],
        onSuccess: (response) => new HandoverPatch({ signatureMatrix: response as any }),
      }),
    ),
  );

  updateFrontPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverUpdateFrontPage>(HandoverActionTypes.HandoverUpdateFrontPage),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId, getCurrentHandoverId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'updateFrontPage',
        serviceArgs: ([action, currentProject, cpId, handoverId]) => [currentProject.id, cpId, handoverId, action.data],
        onSuccess: () => ({ type: '[Handover API] Update Front Page Success' }),
      }),
    ),
  );

  loadRevisions$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverLoadRevisions>(HandoverActionTypes.HandoverLoadRevisions),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!currentProject && typeof cpId === 'number' && Number.isFinite(cpId) && cpId > 0),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'getRevisions',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.handoverId],
        onSuccess: (response) => new HandoverLoadRevisionsSuccess(response as any),
      }),
    ),
  );

  createNewRevision$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverCreateRevision>(HandoverActionTypes.HandoverCreateRevision),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverService,
        method: 'createNewRevision',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.handoverId],
        onSuccess: (response) => new HandoverLoadSuccess([response] as any),
      }),
    ),
  );
}
